import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ErrorState, Input, PageHeader, PageLoader, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { useForm } from '../../../hooks/useForm';
import { BillUpload } from '../../../components/common/BillUpload/BillUpload';
import { purchaseService } from '../services/purchaseService';
import type { FormEvent } from 'react';

/** Only non-money details of a purchase can change. */
export default function PurchaseEdit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const { form, setForm, set } = useForm({ vendorBillNo: '', billPhotoUrl: '', notes: '' });
  const { data: p, error, reload } = useFetch(() => purchaseService.get(id), [id]);
  useCrumb(`/purchases/${id}`, p?.purchaseNo);
  useEffect(() => {
    if (p) setForm({ vendorBillNo: p.vendorBillNo ?? '', billPhotoUrl: p.billPhotoUrl ?? '', notes: p.notes ?? '' });
  }, [p, setForm]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!p) return <PageLoader />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await purchaseService.update(id, { vendorBillNo: form.vendorBillNo.trim(), billPhotoUrl: form.billPhotoUrl, notes: form.notes.trim() });
      toast.success('Purchase updated');
      navigate(`/purchases/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };
  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(`/purchases/${id}`)} disabled={saving}>Cancel</Button>
      <Button type="submit" form="purchase-edit" leftIcon="check" loading={saving}>Save changes</Button>
    </>
  );

  return (
    <>
      <PageHeader title={`Edit ${p.purchaseNo}`} subtitle="Quantities and rates cannot change. Cancel the purchase and enter it again to fix them." actions={actions} />
      <form id="purchase-edit" onSubmit={submit} noValidate className="space-y-5">
        <FormSection title="Bill details">
          <Input label="Supplier bill number" value={form.vendorBillNo} onChange={set('vendorBillNo')} />
          <BillUpload value={form.billPhotoUrl} onChange={set('billPhotoUrl')} />
          <Textarea label="Notes" rows={3} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" />
        </FormSection>
        <div className="flex justify-end gap-2">{actions}</div>
      </form>
    </>
  );
}
